import { API_BASE } from '@/utils/api-url'
import {
  AUTH_EXPIRED_ERROR_MESSAGE,
  extractErrorMessage,
  getHttpErrorMessage,
  getNetworkErrorMessage,
  isApiSuccess,
  isAuthExpiredResponse,
  readResponseJson
} from '@/utils/http-error'

const CLIENT_ID = import.meta.env.VITE_APP_CLIENT_ID || 'app'
const CLIENT_SECRET = import.meta.env.VITE_APP_CLIENT_SECRET || 'app'

/**
 * 发起认证相关 API 请求，并统一解析业务错误。
 * @param {string} path API 路径
 * @param {object} options fetch 请求配置
 * @param {string} fallbackMessage 业务默认错误文案
 * @returns {Promise<*>} 业务数据
 */
async function requestApp(path, options = {}, fallbackMessage = '接口请求失败') {
  let response
  try {
    response = await fetch(`${API_BASE}${path}`, options)
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error))
  }
  const result = await readResponseJson(response)
  if (isAuthExpiredResponse(response, result)) {
    throw new Error(getHttpErrorMessage(response, result, AUTH_EXPIRED_ERROR_MESSAGE))
  }
  if (!response.ok || !isApiSuccess(result)) {
    throw new Error(getHttpErrorMessage(response, result, fallbackMessage))
  }
  return result.data
}

function basicAuthHeader() {
  return `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`
}

/**
 * 使用手机号和密码登录官网。
 * @param {string} phone 手机号
 * @param {string} password 登录密码
 * @returns {Promise<object>} OAuth2 令牌信息
 */
export async function loginWithPassword(phone, password) {
  const body = new URLSearchParams()
  body.set('grant_type', 'app_password')
  body.set('login_channel', 'WEBSITE')
  body.set('mobile', phone)
  body.set('password', password)

  let response
  try {
    response = await fetch(`${API_BASE}/oauth2/token`, {
      method: 'POST',
      headers: {
        Authorization: basicAuthHeader(),
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body
    })
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error))
  }

  const result = await readResponseJson(response)
  if (!response.ok) {
    throw new Error(getHttpErrorMessage(response, result, '登录失败'))
  }
  if (result?.code !== undefined && result.code !== 0) {
    throw new Error(extractErrorMessage(result, '登录失败'))
  }
  if (result?.error && !result?.access_token && !result?.accessToken && !result?.data?.access_token) {
    throw new Error(extractErrorMessage(result, '登录失败'))
  }

  const tokenResponse = result?.access_token || result?.accessToken ? result : result?.data
  const accessToken = tokenResponse?.access_token || tokenResponse?.accessToken
  if (typeof accessToken !== 'string' || !accessToken.trim()) {
    throw new Error('登录响应缺少访问令牌')
  }

  return {
    ...tokenResponse,
    access_token: accessToken.trim()
  }
}

/**
 * 发送官网登录短信验证码。
 * @param {string} phone 手机号
 * @returns {Promise<*>} 发送结果
 */
export async function sendLoginCode(phone) {
  return requestApp('/auth/send-code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ phone, scene: 'login' })
  }, '验证码发送失败')
}

export function sendRegistrationCode(phone) {
  return requestApp('/auth/send-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, scene: 'register' })
  })
}

export function registerAccount(data) {
  return requestApp('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
}

export function getInviterInfo(inviteCode) {
  return requestApp(`/h5/user/by-invite-code/${encodeURIComponent(inviteCode)}`)
}

export function getAgreementContent(key) {
  return requestApp(`/app-agreement/use/${key}`)
}

export function getAppDownloadInfo() {
  return requestApp('/h5/apk-url')
}
